import * as Highcharts from "highcharts";
import {on} from './util';
import { getChartTheme } from './utils/chartTheme';

declare const correlations_data : any;

export default class {
    static init = () => {
        correlations();
    }
}

// Domain color map (UI-SPEC §Highcharts Series Color Palette — domain color map)
const DOMAIN_COLORS: Record<string, string> = {
    alcohol:      '#3BABD3',
    weight:       '#BB72F4',
    runs:         '#0090FF',
    spending:     '#FF5733',
    gym:          '#28a745',
    reading:      '#FFC107',
    manga:        '#E91E63',
    watch_time:   '#9C27B0',
    stairmaster:  '#FF9800',
    scrobbles:    '#E53935',
    mood:         '#F9C74F',  // Phase 173 — warm amber for mood axis
    sick_days:         '#EF4444',  // Phase 188 — illness (matches /oura band legend red)
    caffeine_days:     '#60A5FA',  // Phase 188 — caffeine (matches /oura band legend blue)
    oura_alcohol_days: '#F59E0B',  // Phase 188 — alcohol (matches /oura band legend amber)
};

function correlations() {
    // Guard: only run when chart containers exist (UI-SPEC host-only guard,
    // feedback_shared_component_every_page_init_double_bind)
    const scatterEl  = document.getElementById('chart_scatter');
    const timeseriesEl = document.getElementById('chart_timeseries');
    if (!scatterEl && !timeseriesEl) {
        return;
    }

    if (typeof correlations_data === 'undefined') {
        return;
    }

    // ── Selector round-trip handlers (D-04) ───────────────────────────────────

    // Year dropdown — preserve pair params from current URL
    on('change', 'select[name="corr_year"]', (element: HTMLInputElement) => {
        const params = new URLSearchParams(window.location.search);
        params.set('year', element.value);
        params.delete('from');
        params.delete('to');
        location.href = '?' + params.toString();
    });

    // Custom date range — preserve pair params from current URL
    on('change', 'input[name="corr_from"],input[name="corr_to"]', (element: HTMLInputElement) => {
        let from = element.name === 'corr_from' ? element.value : document.querySelector<HTMLInputElement>('input[name="corr_from"]')?.value;
        let to   = element.name === 'corr_to'   ? element.value : document.querySelector<HTMLInputElement>('input[name="corr_to"]')?.value;
        const params = new URLSearchParams(window.location.search);
        params.delete('year');
        if (from) params.set('from', from);
        if (to)   params.set('to', to);
        location.href = '?' + params.toString();
    });

    // Domain pair dropdowns — build ?pair_a=...&pair_b=... round-trip, preserve year/from/to
    on('change', 'select[name="pair_a"],select[name="pair_b"]', (_element: HTMLInputElement) => {
        const selA = document.querySelector<HTMLSelectElement>('select[name="pair_a"]');
        const selB = document.querySelector<HTMLSelectElement>('select[name="pair_b"]');
        const pairA = selA?.value ?? '';
        const pairB = selB?.value ?? '';
        const params = new URLSearchParams(window.location.search);
        if (pairA) params.set('pair_a', pairA);
        if (pairB) params.set('pair_b', pairB);
        // Remove preset param when manually selecting domains
        params.delete('preset');
        location.href = '?' + params.toString();
    });

    // Preset pills are <a href="?preset=..."> anchors — no JS handler needed (UI-SPEC §Interaction Contract)

    // ── scatterWithTrend factory (D-01, D-03, UI-SPEC §Section 3) ────────────
    function scatterWithTrend(
        container: string,
        title: string,
        labelX: string,
        labelY: string,
        points: Array<{x: number, y: number, week?: string}>,
        fitLine: [[number, number], [number, number]]
    ) {
        const el = document.getElementById(container);
        if (!el) return;

        Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
            chart: {
                type: 'scatter',
                height: 320,
                // No zooming on scatter — avoid accidental touch zoom on mobile (UI-SPEC §Section 3)
            },
            title: {
                text: title,
                style: { fontSize: '14px' },
            },
            xAxis: {
                title: { text: labelX },
                gridLineWidth: 0.1,
            },
            yAxis: {
                title: { text: labelY },
                gridLineWidth: 0.1,
            },
            legend: {
                enabled: true,
            },
            series: [
                {
                    type: 'scatter',
                    name: labelX + ' vs ' + labelY,
                    data: points,
                    color: '#3BABD3',
                    marker: { radius: 4, symbol: 'circle' },
                },
                {
                    type: 'line',
                    name: 'Trend',
                    data: fitLine,
                    color: '#FF7F50',
                    lineWidth: 2,
                    dashStyle: 'ShortDash',
                    marker: { enabled: false },
                    enableMouseTracking: false,
                },
            ],
            plotOptions: {
                scatter: {
                    tooltip: {
                        headerFormat: '',
                        pointFormat: labelX + ': <b>{point.x:.2f}</b><br>' +
                                     labelY + ': <b>{point.y:.2f}</b><br>' +
                                     'Week of: <b>{point.week}</b>',
                    },
                },
            },
            credits: { enabled: false },
        } as any));
    }

    // ── dualAxisChart factory (D-01, UI-SPEC §Section 4) — copied from alcohol.ts L288-305
    function dualAxisChart(
        container: string,
        title: string,
        series1Name: string,
        series1Data: any[],
        series1Color: string,
        series2Name: string,
        series2Data: any[],
        series2Color: string
    ) {
        const el = document.getElementById(container);
        if (!el) return;

        // Build timestamps from correlations_data.series.week_starts
        const dates = (correlations_data.series?.week_starts ?? []).map((d: string) => Date.parse(d));

        Highcharts.chart(container, Highcharts.merge(getChartTheme(), {
            chart: { height: 320, zooming: { type: 'x' } },
            title: { text: title, style: { fontSize: '14px' } },
            xAxis: { type: 'datetime' },
            yAxis: [
                { title: { text: series1Name, style: { color: series1Color } }, gridLineWidth: 0.1 },
                { title: { text: series2Name, style: { color: series2Color } }, gridLineWidth: 0.1, opposite: true },
            ],
            series: [
                { type: 'column', name: series1Name, data: series1Data.map((v: any, i: number) => [dates[i], v]), color: series1Color, yAxis: 0 },
                { type: 'spline', name: series2Name, data: series2Data.map((v: any, i: number) => [dates[i], v]), color: series2Color, yAxis: 1, connectNulls: true },
            ],
            plotOptions: { column: { borderWidth: 0 }, spline: { lineWidth: 2, marker: { enabled: false } } },
            credits: { enabled: false },
        } as any));
    }

    // ── Render charts ─────────────────────────────────────────────────────────

    // Determine domain colors from correlations_data labels (fall back to defaults)
    const pairA: string = correlations_data.pair_a ?? 'alcohol';
    const pairB: string = correlations_data.pair_b ?? 'weight';
    const colorA = DOMAIN_COLORS[pairA] ?? '#3BABD3';
    const colorB = DOMAIN_COLORS[pairB] ?? '#BB72F4';
    const labelA: string = correlations_data.label_a ?? pairA;
    const labelB: string = correlations_data.label_b ?? pairB;

    // Section 3: Scatter + trend line
    if (scatterEl && Array.isArray(correlations_data.points)) {
        const fitLine = Array.isArray(correlations_data.fit_line) && correlations_data.fit_line.length === 2
            ? correlations_data.fit_line as [[number, number], [number, number]]
            : [[0, 0], [0, 0]] as [[number, number], [number, number]];

        scatterWithTrend(
            'chart_scatter',
            labelA + ' vs ' + labelB + ' (weekly)',
            labelA,
            labelB,
            correlations_data.points,
            fitLine
        );
    }

    // Section 4: Dual-axis time-series
    if (timeseriesEl && correlations_data.series) {
        dualAxisChart(
            'chart_timeseries',
            labelA + ' + ' + labelB + ' over time',
            labelA,
            correlations_data.series.a ?? [],
            colorA,
            labelB,
            correlations_data.series.b ?? [],
            colorB
        );
    }
}
